import { useSite } from "@/context/SiteContext";
import { Section, GoldButton } from "@/components/site/Ui";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import EnquiryForm from "@/components/site/EnquiryForm";
import { Send, Crown, Check } from "lucide-react";

const PLATFORMS = ["YouTube", "TikTok", "Instagram", "Facebook", "Other"];
const PERKS = ["Extra 1:1 support", "Live Q&A sessions", "Weekly tips", "Accountability", "Member-only resources"];

export default function Community() {
  const { settings } = useSite();

  return (
    <>
      <div className="relative grain overflow-hidden">
        <Section className="pb-8">
          <Reveal>
            <Eyebrow>Community</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.05]">You don't have to do this alone.</h1>
            <p className="mt-5 text-white/70 max-w-xl text-lg">Join creators who are learning together, cheering each other on, and staying consistent.</p>
          </Reveal>
        </Section>
      </div>

      <Section className="pt-4 grid lg:grid-cols-2 gap-8">
        {/* Free Telegram */}
        <Reveal>
          <div className="card-surface rounded-3xl p-8 h-full flex flex-col">
            <div className="flex items-center gap-2 text-[#D4AF37]"><Send size={20} /><span className="text-xs uppercase tracking-widest">Free · Telegram</span></div>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl">Free Community</h2>
            <p className="mt-3 text-white/65 leading-relaxed">A friendly, free space to ask questions, share your wins, and get encouragement while you start out. Perfect for total beginners.</p>
            {settings.telegram_link_enabled && settings.telegram_link ? (
              <GoldButton as="a" href={settings.telegram_link} target="_blank" rel="noreferrer" className="mt-6 w-full sm:w-auto">Join on Telegram</GoldButton>
            ) : (
              <div className="mt-6">
                <p className="text-sm text-white/50 mb-4">Request to join and we'll send you the link.</p>
                <EnquiryForm testid="telegram-form" type="community-telegram" submitLabel="Request to Join"
                  successMsg="Request received! We'll review and send you the Telegram link soon."
                  fields={[
                    { name: "name", label: "Name", required: true },
                    { name: "email", label: "Email", type: "email", required: true },
                    { name: "telegram", label: "Telegram username" },
                    { name: "platform", label: "Platform of interest", type: "select", options: PLATFORMS },
                  ]} />
              </div>
            )}
          </div>
        </Reveal>

        {/* Paid WhatsApp */}
        <Reveal delay={0.1}>
          <div className="rounded-3xl p-8 h-full flex flex-col border-2 border-[#D4AF37] gold-glow">
            <div className="flex items-center gap-2 text-[#D4AF37]"><Crown size={20} /><span className="text-xs uppercase tracking-widest">Premium · WhatsApp</span></div>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl">Paid Membership Community</h2>
            {settings.whatsapp_community_price ? (
              <div className="mt-2 flex items-end gap-1.5">
                <span className="text-3xl font-display gold-text">£{settings.whatsapp_community_price}</span>
                <span className="text-white/40 text-sm mb-1">/ month</span>
              </div>
            ) : null}
            <p className="mt-3 text-white/65 leading-relaxed">{settings.whatsapp_community_details || "A closer, premium space with more support and accountability."}</p>
            <ul className="mt-5 space-y-2">
              {PERKS.map((p) => <li key={p} className="flex gap-2.5 text-sm text-white/80"><Check size={17} className="text-[#D4AF37] shrink-0 mt-0.5" />{p}</li>)}
            </ul>
            <p className="mt-5 text-sm text-white/50">Joining is by request. We'll send you the details and payment link after approval.</p>
            <div className="mt-6">
              <EnquiryForm testid="whatsapp-community-form" type="community-whatsapp" submitLabel="Request to Join"
                successMsg="Request received! We'll be in touch with the details."
                fields={[
                  { name: "name", label: "Name", required: true },
                  { name: "email", label: "Email", type: "email", required: true },
                  { name: "whatsapp", label: "WhatsApp number", required: true },
                  { name: "platform", label: "Platform of interest", type: "select", options: PLATFORMS },
                ]} />
            </div>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
